import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar } from '@/components/data-display/avatar'
import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { SearchInput } from '@/components/forms/search-input'
import { Select, type SelectOption } from '@/components/forms/select'
import { Drawer } from '@/components/overlays/drawer'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'
import { ApiError } from '@/lib/api/http-client'
import { cn } from '@/lib/cn'
import { getOrganizationTree, listMemberAccounts } from '@/features/admin/organization/api'

import {
  batchBindAdminAppMembers,
  batchUnbindAdminAppMembers,
  bindAdminAppDepartment,
  createOrUpdateAdminAppMember,
  getAdminAppMembersPermissionTree,
  listAdminCompanies,
  unbindAdminAppDepartment,
  type AdminApp,
  type AdminAppMemberBatchResult,
  type AdminCompany,
} from './api'
import {
  buildMemberPermissionChanges,
  collectExpandableOrgIds,
  collectInitialPermissionState,
  collectNodeMemberIds,
  flattenMembers,
  flattenOrganizations,
  getOrganizationChildren,
  normalizeOrganizationTree,
  resolveOrgPathName,
  type MemberIndexItem,
  type OrganizationNode,
  type PermissionState,
} from './members-model'

export interface AdminAppMemberScopeDraft {
  accountIds: string[]
  effectiveMemberCount: number
  excludedAccountIds: string[]
  orgUnitIds: string[]
}

export type AdminAppMemberScopeDrafts = Record<string, AdminAppMemberScopeDraft>

interface AdminAppMembersDrawerProps {
  app: AdminApp | null
  embedded?: boolean
  embeddedDrafts?: AdminAppMemberScopeDrafts
  onDraftChange?: (companyId: string, draft: AdminAppMemberScopeDraft) => void
  onOpenChange: (open: boolean) => void
  open: boolean
}

type SelectedPanel = 'departments' | 'members'

const memberPageSize = 20

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden className={cn('size-3.5', className)} fill="none" viewBox="0 0 14 14">
      <path d="m5 3 4 4-4 4" stroke="currentColor" strokeLinecap="round" strokeWidth="1.6" />
    </svg>
  )
}

function FolderIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden className={cn('size-4', className)} fill="none" viewBox="0 0 16 16">
      <path
        d="M2.5 4.6c0-.83.67-1.5 1.5-1.5h2.05c.42 0 .82.18 1.1.49l.66.72H12c.83 0 1.5.67 1.5 1.5v5.6c0 .83-.67 1.5-1.5 1.5H4c-.83 0-1.5-.67-1.5-1.5V4.6Z"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.4"
      />
    </svg>
  )
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden className={cn('size-5', className)} fill="none" viewBox="0 0 20 20">
      <path
        d="M7.8 9.4a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm0 2.15c-2.86 0-5.1 1.38-5.1 3.14 0 .5.4.91.9.91H12c.5 0 .9-.4.9-.9 0-1.77-2.24-3.15-5.1-3.15Zm5.85-1.85a2.25 2.25 0 1 0 0-4.5m.35 6.2c1.94.33 3.3 1.36 3.3 2.58 0 .42-.34.77-.77.77h-1.8"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden className={cn('size-3.5', className)} fill="none" viewBox="0 0 14 14">
      <path d="m3 7 2.5 2.5L11 4" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  )
}

function MinusIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden className={cn('size-3.5', className)} fill="none" viewBox="0 0 14 14">
      <path d="M3.25 7h7.5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  )
}

function SelectionMark({ active, state }: { active: boolean; state: PermissionState }) {
  const isChecked = state === 'checked'
  const isPartial = state === 'indeterminate'

  return (
    <span
      className={cn(
        'inline-flex size-4 items-center justify-center rounded-[4px] border transition-colors',
        active
          ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
          : 'border-[rgba(26,28,25,0.22)] bg-white text-[rgba(26,28,25,0.48)]',
        !active && (isChecked || isPartial) ? 'border-[rgba(26,28,25,0.34)] bg-[#f3f5f8]' : null,
      )}
    >
      {isChecked ? <CheckIcon /> : null}
      {isPartial ? <MinusIcon /> : null}
    </span>
  )
}

function MemberSelectionDot({ active }: { active: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors',
        active
          ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
          : 'border-[rgba(26,28,25,0.2)] bg-white text-transparent',
      )}
    >
      {active ? <CheckIcon className="size-3" /> : null}
    </span>
  )
}

function formatApiError(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    return error.message
  }

  return fallback
}

function sortOrganizations(
  nodes: OrganizationNode[],
  compareOrgNames: (a: OrganizationNode, b: OrganizationNode) => number,
) {
  return [...nodes].sort(compareOrgNames)
}

function countBatchFailures(results: AdminAppMemberBatchResult[]) {
  return results.reduce((count, result) => count + result.failed_count, 0)
}

function countEffectiveMembers(
  selectedMemberIds: ReadonlySet<string>,
  inheritedMemberIds: ReadonlySet<string>,
  excludedMemberIds: ReadonlySet<string>,
) {
  const ids = new Set<string>()

  selectedMemberIds.forEach((id) => ids.add(id))
  inheritedMemberIds.forEach((id) => {
    if (!selectedMemberIds.has(id) && !excludedMemberIds.has(id)) {
      ids.add(id)
    }
  })

  return ids.size
}

function PaneEmpty({ children, icon }: { children: string; icon?: 'users' }) {
  return (
    <div className="flex h-full min-h-32 flex-col items-center justify-center px-5 py-8 text-center text-sm text-[var(--color-text-subtle)]">
      {icon === 'users' ? <UsersIcon className="mb-3 size-8 text-[rgba(26,28,25,0.18)]" /> : null}
      {children}
    </div>
  )
}

interface OrganizationTreeNodeProps {
  compareOrgNames: (a: OrganizationNode, b: OrganizationNode) => number
  depth: number
  expandedOrgIds: ReadonlySet<string>
  highlightedOrgId: string | null
  memberSelectionStateMap: Map<string, PermissionState>
  node: OrganizationNode
  onSelect: (node: OrganizationNode) => void
  onToggleExpand: (nodeId: string) => void
  onToggleOrg: (node: OrganizationNode, checked: boolean) => void
  orgSelectionStateMap: Map<string, PermissionState>
  selectedDeptId: string | null
  t: (key: string, options?: Record<string, unknown>) => string
}

function OrganizationTreeNode({
  compareOrgNames,
  depth,
  expandedOrgIds,
  highlightedOrgId,
  memberSelectionStateMap,
  node,
  onSelect,
  onToggleExpand,
  onToggleOrg,
  orgSelectionStateMap,
  selectedDeptId,
  t,
}: OrganizationTreeNodeProps) {
  if (node.type === 'member') {
    return null
  }

  const children = sortOrganizations(getOrganizationChildren(node), compareOrgNames)
  const isExpanded = expandedOrgIds.has(node.id)
  const isSelected = selectedDeptId === node.id
  const isHighlighted = highlightedOrgId === node.id
  const orgState = orgSelectionStateMap.get(node.id) ?? 'unchecked'
  const memberState = memberSelectionStateMap.get(node.id) ?? 'unchecked'
  const isOrgActive = orgState !== 'unchecked'

  return (
    <div className="select-none">
      <div
        className={cn(
          'group flex min-h-9 items-center rounded-md pr-1 transition-colors',
          isSelected ? 'bg-[rgba(25,91,255,0.1)]' : 'hover:bg-[rgba(26,28,25,0.05)]',
          isHighlighted ? 'ring-2 ring-[var(--color-primary-soft)]' : null,
        )}
        id={`admin-app-org-${node.id}`}
        style={{ paddingLeft: `${String(depth * 12 + 6)}px` }}
      >
        <button
          aria-label={
            isExpanded
              ? t('membersDrawer.tree.collapse', { name: node.name })
              : t('membersDrawer.tree.expand', { name: node.name })
          }
          className={cn(
            'flex size-7 shrink-0 cursor-pointer items-center justify-center rounded text-[var(--color-text-subtle)] transition-colors hover:bg-[var(--color-hover-muted)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-focus)]',
            children.length === 0 ? 'invisible' : null,
          )}
          onClick={() => onToggleExpand(node.id)}
          type="button"
        >
          <ChevronIcon className={cn('transition-transform', isExpanded ? 'rotate-90' : null)} />
        </button>
        <button
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 py-2 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-focus)]"
          onClick={() => onSelect(node)}
          type="button"
        >
          <FolderIcon
            className={cn(
              'shrink-0',
              isSelected ? 'text-[var(--color-primary)]' : 'text-[var(--color-text-subtle)]',
            )}
          />
          <span
            className={cn(
              'truncate text-sm',
              isSelected ? 'font-semibold text-[var(--color-primary)]' : 'text-[var(--color-text)]',
            )}
            title={node.name}
          >
            {node.name}
          </span>
          {typeof node.memberCount === 'number' && node.memberCount > 0 ? (
            <span className="shrink-0 text-xs text-[var(--color-text-subtle)]">
              {node.memberCount}
            </span>
          ) : null}
        </button>
        <button
          aria-label={
            isOrgActive
              ? t('membersDrawer.tree.unbindDepartment', { name: node.name })
              : t('membersDrawer.tree.bindDepartment', { name: node.name })
          }
          aria-pressed={isOrgActive}
          className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded transition-transform hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-focus)]"
          onClick={() => onToggleOrg(node, orgState !== 'checked')}
          type="button"
        >
          <SelectionMark active={isOrgActive} state={isOrgActive ? orgState : memberState} />
        </button>
      </div>
      {isExpanded
        ? children.map((child) => (
            <OrganizationTreeNode
              compareOrgNames={compareOrgNames}
              depth={depth + 1}
              expandedOrgIds={expandedOrgIds}
              highlightedOrgId={highlightedOrgId}
              key={child.id}
              memberSelectionStateMap={memberSelectionStateMap}
              node={child}
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
              onToggleOrg={onToggleOrg}
              orgSelectionStateMap={orgSelectionStateMap}
              selectedDeptId={selectedDeptId}
              t={t}
            />
          ))
        : null}
    </div>
  )
}

export function AdminAppMembersDrawer({
  app,
  embedded = false,
  embeddedDrafts,
  onDraftChange,
  onOpenChange,
  open,
}: AdminAppMembersDrawerProps) {
  const { t } = useTranslation('adminApps')
  const queryClient = useQueryClient()
  const { showFeedback } = useFeedback()
  const [selectedCompanyId, setSelectedCompanyId] = useState('')
  const [selectedDeptId, setSelectedDeptId] = useState<string | null>(null)
  const [expandedOrgIds, setExpandedOrgIds] = useState<Set<string>>(new Set())
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set())
  const [selectedOrgIds, setSelectedOrgIds] = useState<Set<string>>(new Set())
  const [excludedMemberIds, setExcludedMemberIds] = useState<Set<string>>(new Set())
  const [initialMemberIds, setInitialMemberIds] = useState<Set<string>>(new Set())
  const [initialOrgIds, setInitialOrgIds] = useState<Set<string>>(new Set())
  const [initialExcludedMemberIds, setInitialExcludedMemberIds] = useState<Set<string>>(new Set())
  const [orgSearch, setOrgSearch] = useState('')
  const [memberSearch, setMemberSearch] = useState('')
  const [memberPage, setMemberPage] = useState(1)
  const [highlightedOrgId, setHighlightedOrgId] = useState<string | null>(null)
  const [orgSortDirection, setOrgSortDirection] = useState<'asc' | 'desc'>('asc')
  const [selectedPanel, setSelectedPanel] = useState<SelectedPanel>('members')
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [initializedCompanyId, setInitializedCompanyId] = useState('')

  const companiesQuery = useQuery({
    enabled: open,
    queryFn: listAdminCompanies,
    queryKey: ['admin-companies'],
  })

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      if (!open) {
        setSelectedCompanyId('')
        setInitializedCompanyId('')
        setSubmitError('')
        return
      }

      const companies = companiesQuery.data ?? []

      if (!selectedCompanyId && companies.length > 0) {
        setSelectedCompanyId(companies[0].id)
      }
    }, 0)

    return () => window.clearTimeout(timeoutId)
  }, [companiesQuery.data, open, selectedCompanyId])

  const treeQuery = useQuery({
    enabled: open && Boolean(selectedCompanyId) && (embedded || Boolean(app?.id)),
    queryFn: () =>
      embedded
        ? getOrganizationTree(selectedCompanyId)
        : getAdminAppMembersPermissionTree(app?.id ?? '', selectedCompanyId),
    queryKey: embedded
      ? ['admin-organization-tree', selectedCompanyId]
      : ['admin-app-members-tree', app?.id, selectedCompanyId],
  })

  const root = useMemo(
    () => (treeQuery.data ? normalizeOrganizationTree(treeQuery.data) : null),
    [treeQuery.data],
  )

  useEffect(() => {
    if (!open || !root) {
      return undefined
    }

    if (embedded && (root.id !== selectedCompanyId || initializedCompanyId === selectedCompanyId)) {
      return undefined
    }

    const initial = collectInitialPermissionState(root)
    const embeddedDraft = embedded ? embeddedDrafts?.[selectedCompanyId] : undefined

    const timeoutId = window.setTimeout(() => {
      setSelectedDeptId(root.id)
      setExpandedOrgIds(collectExpandableOrgIds(root))
      setSelectedMemberIds(new Set(embeddedDraft?.accountIds ?? initial.memberIds))
      setSelectedOrgIds(new Set(embeddedDraft?.orgUnitIds ?? initial.orgIds))
      setExcludedMemberIds(new Set(embeddedDraft?.excludedAccountIds ?? initial.excludedMemberIds))
      setInitialMemberIds(new Set(initial.memberIds))
      setInitialOrgIds(new Set(initial.orgIds))
      setInitialExcludedMemberIds(new Set(initial.excludedMemberIds))
      setOrgSearch('')
      setMemberSearch('')
      setMemberPage(1)
      setHighlightedOrgId(null)
      setSelectedPanel('members')
      setSubmitError('')
      setInitializedCompanyId(selectedCompanyId)
    }, 0)

    return () => window.clearTimeout(timeoutId)
  }, [embedded, embeddedDrafts, initializedCompanyId, open, root, selectedCompanyId])

  const orgNameCollator = useMemo(() => {
    try {
      return new Intl.Collator('zh-Hans-u-co-pinyin', { sensitivity: 'base' })
    } catch {
      return null
    }
  }, [])

  const compareOrgNames = useCallback(
    (a: OrganizationNode, b: OrganizationNode) => {
      const nameA = a.name || ''
      const nameB = b.name || ''
      let result = orgNameCollator
        ? orgNameCollator.compare(nameA, nameB)
        : nameA.localeCompare(nameB, 'zh-Hans-CN', { sensitivity: 'base' })

      if (result === 0) {
        result = nameA.localeCompare(nameB, 'zh-Hans-CN')
      }

      if (result === 0) {
        result = a.id.localeCompare(b.id)
      }

      return orgSortDirection === 'asc' ? result : -result
    },
    [orgNameCollator, orgSortDirection],
  )

  const orgMap = useMemo<Map<string, OrganizationNode>>(
    () => (root ? flattenOrganizations(root) : new Map<string, OrganizationNode>()),
    [root],
  )
  const memberMap = useMemo<Map<string, MemberIndexItem>>(
    () => (root ? flattenMembers(root) : new Map<string, MemberIndexItem>()),
    [root],
  )
  const nodeMemberIdsMap = useMemo<Map<string, string[]>>(
    () => (root ? collectNodeMemberIds(root) : new Map<string, string[]>()),
    [root],
  )
  const selectedDept = selectedDeptId ? orgMap.get(selectedDeptId) : (root ?? undefined)
  const membersQuery = useQuery({
    enabled: open && Boolean(selectedDept?.id),
    queryFn: () =>
      listMemberAccounts({
        orgUnitId: selectedDept?.id,
        page: memberPage,
        pageSize: memberPageSize,
        search: memberSearch,
        status: 'active',
      }),
    queryKey: [
      'admin-organization-member-accounts',
      'app-members-drawer',
      selectedDept?.id,
      memberPage,
      memberSearch,
    ],
  })
  const visibleMembers = useMemo(() => membersQuery.data?.items ?? [], [membersQuery.data?.items])
  const memberTotal = membersQuery.data?.total ?? 0
  const memberPageCount = Math.max(1, Math.ceil(memberTotal / memberPageSize))

  const inheritedMemberIds = useMemo(() => {
    const ids = new Set<string>()

    selectedOrgIds.forEach((orgId) => {
      nodeMemberIdsMap.get(orgId)?.forEach((memberId) => ids.add(memberId))
    })

    return ids
  }, [nodeMemberIdsMap, selectedOrgIds])

  const effectiveSelectedMemberIds = useMemo(() => {
    const ids = new Set<string>()

    selectedMemberIds.forEach((id) => ids.add(id))
    inheritedMemberIds.forEach((id) => {
      if (!selectedMemberIds.has(id) && !excludedMemberIds.has(id)) {
        ids.add(id)
      }
    })

    return ids
  }, [excludedMemberIds, inheritedMemberIds, selectedMemberIds])

  const orgSelectionStateMap = useMemo(() => {
    const map = new Map<string, PermissionState>()

    if (!root) {
      return map
    }

    function visit(node: OrganizationNode): { allSelected: boolean; anySelected: boolean } {
      if (node.type === 'member') {
        return { allSelected: false, anySelected: false }
      }

      const orgChildren = getOrganizationChildren(node)
      const childStates = orgChildren.map(visit)
      const anyChildSelected = childStates.some((state) => state.anySelected)
      const allChildrenSelected =
        orgChildren.length === 0 || childStates.every((state) => state.allSelected)
      const selfSelected = node.type !== 'company' && selectedOrgIds.has(node.id)
      const anySelected = selfSelected || anyChildSelected
      const allSelected =
        node.type === 'company'
          ? orgChildren.length > 0 && allChildrenSelected
          : selfSelected && allChildrenSelected

      map.set(node.id, allSelected ? 'checked' : anySelected ? 'indeterminate' : 'unchecked')

      return {
        allSelected,
        anySelected,
      }
    }

    visit(root)

    return map
  }, [root, selectedOrgIds])

  const memberSelectionStateMap = useMemo(() => {
    const map = new Map<string, PermissionState>()

    nodeMemberIdsMap.forEach((memberIds, nodeId) => {
      if (memberIds.length === 0) {
        map.set(nodeId, 'unchecked')
        return
      }

      const selectedCount = memberIds.filter((memberId) =>
        effectiveSelectedMemberIds.has(memberId),
      ).length

      map.set(
        nodeId,
        selectedCount === 0
          ? 'unchecked'
          : selectedCount === memberIds.length
            ? 'checked'
            : 'indeterminate',
      )
    })

    return map
  }, [effectiveSelectedMemberIds, nodeMemberIdsMap])

  const pendingChanges = useMemo(() => {
    if (!root) {
      return null
    }

    return buildMemberPermissionChanges({
      excludedMemberIds,
      initialExcludedMemberIds,
      initialMemberIds,
      initialOrgIds,
      orgMap,
      selectedMemberIds,
      selectedOrgIds,
    })
  }, [
    excludedMemberIds,
    initialExcludedMemberIds,
    initialMemberIds,
    initialOrgIds,
    orgMap,
    root,
    selectedMemberIds,
    selectedOrgIds,
  ])

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!app?.id || !pendingChanges?.hasChanges) {
        return { failedCount: 0 }
      }

      const operations: Promise<unknown>[] = []
      const batchResults: AdminAppMemberBatchResult[] = []
      const addBatchOperation = (operation: Promise<AdminAppMemberBatchResult>) => {
        operations.push(
          operation.then((result) => {
            batchResults.push(result)
            return result
          }),
        )
      }

      if (pendingChanges.accountIdsToUnbind.length > 0) {
        const result = await batchUnbindAdminAppMembers(app.id, pendingChanges.accountIdsToUnbind)
        batchResults.push(result)
      }

      if (pendingChanges.memberToAdd.length > 0) {
        addBatchOperation(batchBindAdminAppMembers(app.id, pendingChanges.memberToAdd))
      }

      pendingChanges.excludedToAdd.forEach((accountId) => {
        operations.push(
          createOrUpdateAdminAppMember({
            accountId,
            appId: app.id,
            excluded: true,
          }),
        )
      })

      pendingChanges.orgToAdd.forEach((orgUnitId) => {
        operations.push(bindAdminAppDepartment(app.id, orgUnitId))
      })

      pendingChanges.orgToRemove.forEach((orgUnitId) => {
        operations.push(unbindAdminAppDepartment(app.id, orgUnitId))
      })

      await Promise.all(operations)
      return { failedCount: countBatchFailures(batchResults) }
    },
    onError: (error) => {
      setSubmitError(formatApiError(error, t('membersDrawer.saveFailed')))
    },
    onSuccess: ({ failedCount }) => {
      if (failedCount > 0) {
        const message = t('membersDrawer.partialSaveFailed', { count: failedCount })
        setSubmitError(message)
        showFeedback({
          detail: message,
          message: t('membersDrawer.partialSaveFailedTitle'),
          scope: 'drawer',
          tone: 'warning',
        })
        return
      }

      setSubmitError('')
      showFeedback({
        message: t('membersDrawer.saveSuccess'),
        scope: 'global',
        tone: 'success',
      })
      void queryClient.invalidateQueries({ queryKey: ['admin-app-members-tree', app?.id] })
      onOpenChange(false)
    },
  })

  const effectiveMemberCount = countEffectiveMembers(
    selectedMemberIds,
    inheritedMemberIds,
    excludedMemberIds,
  )

  useEffect(() => {
    if (!embedded || !root || !onDraftChange || initializedCompanyId !== selectedCompanyId) {
      return
    }

    onDraftChange(selectedCompanyId, {
      accountIds: Array.from(selectedMemberIds),
      effectiveMemberCount,
      excludedAccountIds: Array.from(excludedMemberIds),
      orgUnitIds: Array.from(selectedOrgIds),
    })
  }, [
    effectiveMemberCount,
    embedded,
    excludedMemberIds,
    initializedCompanyId,
    onDraftChange,
    root,
    selectedCompanyId,
    selectedMemberIds,
    selectedOrgIds,
  ])

  const selectedUsersForPanel = useMemo(
    () =>
      Array.from(selectedMemberIds)
        .map((id) => {
          const info = memberMap.get(id)

          if (!info) {
            return null
          }

          const deptIds = Array.from(info.deptIds).sort((a, b) =>
            resolveOrgPathName(a, orgMap).localeCompare(
              resolveOrgPathName(b, orgMap),
              'zh-Hans-CN',
            ),
          )

          return {
            deptPath: deptIds.map((deptId) => resolveOrgPathName(deptId, orgMap)).find(Boolean),
            id,
            member: info.member,
            primaryDeptId: deptIds[0],
          }
        })
        .filter((item): item is NonNullable<typeof item> => Boolean(item))
        .sort((a, b) => a.member.name.localeCompare(b.member.name, 'zh-Hans-CN')),
    [memberMap, orgMap, selectedMemberIds],
  )

  const selectedOrgsForPanel = useMemo(
    () =>
      Array.from(selectedOrgIds)
        .map((id) => {
          const node = orgMap.get(id)

          if (!node || node.type === 'company' || node.type === 'member') {
            return null
          }

          return {
            id,
            name: node.name,
            path: resolveOrgPathName(id, orgMap),
          }
        })
        .filter((item): item is NonNullable<typeof item> => Boolean(item))
        .sort((a, b) => a.path.localeCompare(b.path, 'zh-Hans-CN')),
    [orgMap, selectedOrgIds],
  )

  const orgSearchResults = useMemo(() => {
    const query = orgSearch.trim().toLowerCase()

    if (!query) {
      return []
    }

    return Array.from(orgMap.values())
      .filter((node) => {
        if (node.type === 'member') {
          return false
        }

        const path = resolveOrgPathName(node.id, orgMap)

        return node.name.toLowerCase().includes(query) || path.toLowerCase().includes(query)
      })
      .map((node) => ({
        id: node.id,
        name: node.name,
        path: resolveOrgPathName(node.id, orgMap),
      }))
      .sort((a, b) => a.path.localeCompare(b.path, 'zh-Hans-CN'))
      .slice(0, 20)
  }, [orgMap, orgSearch])

  useEffect(() => {
    if (!highlightedOrgId) {
      return undefined
    }

    document
      .getElementById(`admin-app-org-${highlightedOrgId}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })

    const timeoutId = window.setTimeout(() => setHighlightedOrgId(null), 1800)

    return () => window.clearTimeout(timeoutId)
  }, [highlightedOrgId])

  function handleToggleExpand(nodeId: string) {
    setExpandedOrgIds((current) => {
      const next = new Set(current)

      if (next.has(nodeId)) {
        next.delete(nodeId)
      } else {
        next.add(nodeId)
      }

      return next
    })
  }

  function expandOrgPath(nodeId: string) {
    setExpandedOrgIds((current) => {
      const next = new Set(current)
      let currentNode = orgMap.get(nodeId)
      const visited = new Set<string>()

      while (currentNode && !visited.has(currentNode.id)) {
        visited.add(currentNode.id)
        next.add(currentNode.id)

        if (!currentNode.parentId) {
          break
        }

        currentNode = orgMap.get(currentNode.parentId)
      }

      return next
    })
  }

  function handleToggleOrg(node: OrganizationNode, checked: boolean) {
    const previousSelectedOrgIds = selectedOrgIds
    const nextOrgIds = new Set(selectedOrgIds)

    function toggleSubtree(current: OrganizationNode) {
      if (current.type === 'member') {
        return
      }

      if (current.type !== 'company') {
        if (checked) {
          nextOrgIds.add(current.id)
        } else {
          nextOrgIds.delete(current.id)
        }
      }

      current.children?.forEach(toggleSubtree)
    }

    toggleSubtree(node)
    setSelectedOrgIds(nextOrgIds)

    if (checked) {
      const newlySelectedOrgIds = Array.from(nextOrgIds).filter(
        (orgId) => !previousSelectedOrgIds.has(orgId),
      )

      if (newlySelectedOrgIds.length > 0) {
        setSelectedMemberIds((current) => {
          const next = new Set(current)

          newlySelectedOrgIds.forEach((orgId) => {
            nodeMemberIdsMap.get(orgId)?.forEach((memberId) => next.delete(memberId))
          })

          return next
        })
      }
    }
  }

  function handleToggleMember(memberId: string) {
    const nextSelected = new Set(selectedMemberIds)
    const nextExcluded = new Set(excludedMemberIds)
    const isInherited = inheritedMemberIds.has(memberId)
    const isExcluded = nextExcluded.has(memberId)
    const isEffectivelySelected = effectiveSelectedMemberIds.has(memberId)

    if (isEffectivelySelected) {
      nextSelected.delete(memberId)

      if (isInherited) {
        nextExcluded.add(memberId)
      }
    } else if (isInherited && isExcluded) {
      nextExcluded.delete(memberId)
    } else {
      nextSelected.add(memberId)
    }

    setSelectedMemberIds(nextSelected)
    setExcludedMemberIds(nextExcluded)
  }

  function handleSelectAllInView() {
    const nextSelected = new Set(selectedMemberIds)
    const nextExcluded = new Set(excludedMemberIds)

    visibleMembers.forEach((member) => {
      if (inheritedMemberIds.has(member.id)) {
        nextExcluded.delete(member.id)
      } else {
        nextSelected.add(member.id)
      }
    })

    setSelectedMemberIds(nextSelected)
    setExcludedMemberIds(nextExcluded)
  }

  function handleDeselectAllInView() {
    const nextSelected = new Set(selectedMemberIds)
    const nextExcluded = new Set(excludedMemberIds)

    visibleMembers.forEach((member) => {
      nextSelected.delete(member.id)

      if (inheritedMemberIds.has(member.id)) {
        nextExcluded.add(member.id)
      }
    })

    setSelectedMemberIds(nextSelected)
    setExcludedMemberIds(nextExcluded)
  }

  const companyOptions: SelectOption[] = useMemo(
    () =>
      (companiesQuery.data ?? []).map((company: AdminCompany) => ({
        label: company.name,
        value: company.id,
      })),
    [companiesQuery.data],
  )

  const allMembersInViewSelected =
    visibleMembers.length > 0 &&
    visibleMembers.every((member) => effectiveSelectedMemberIds.has(member.id))

  const isInitialLoading =
    companiesQuery.isLoading || (Boolean(selectedCompanyId) && treeQuery.isLoading)
  const loadError = companiesQuery.error ?? treeQuery.error
  const showLoadError = Boolean(loadError) && !root
  const title = app?.name
    ? t('membersDrawer.titleWithName', { name: app.name })
    : t('membersDrawer.title')
  const canSave = Boolean(root) && !isInitialLoading
  const hasUnsavedChanges = Boolean(pendingChanges?.hasChanges)

  function confirmBeforeClosingDrawer() {
    if (!hasUnsavedChanges) {
      return true
    }

    if (!discardDialogOpen) {
      setDiscardDialogOpen(true)
    }

    return false
  }

  function requestCloseDrawer() {
    if (confirmBeforeClosingDrawer()) {
      onOpenChange(false)
    }
  }

  function confirmDiscardChanges() {
    setDiscardDialogOpen(false)
    onOpenChange(false)
  }

  const footer = (
    <>
      <Button
        className="h-[38px] min-w-[60px] rounded-md border-[var(--color-control-border)] px-4 font-normal"
        disabled={saveMutation.isPending}
        onClick={requestCloseDrawer}
        size="sm"
        variant="secondary"
      >
        {t('membersDrawer.cancel')}
      </Button>
      <Button
        className="h-[38px] min-w-[60px] rounded-md px-4 font-normal"
        disabled={!canSave}
        isLoading={saveMutation.isPending}
        onClick={() => saveMutation.mutate()}
        size="sm"
      >
        {saveMutation.isPending ? t('membersDrawer.saving') : t('membersDrawer.save')}
      </Button>
    </>
  )

  const scopeContent = (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col bg-white',
        embedded
          ? 'min-h-[340px] overflow-hidden rounded-lg border border-[var(--color-divider)]'
          : null,
      )}
    >
      <div className="flex min-h-[52px] shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-[var(--color-divider)] px-4 py-2">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          <p className="shrink-0 text-sm font-semibold text-[var(--color-text)]">
            {t('membersDrawer.scopeTitle')}
          </p>
          <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs text-[var(--color-text-muted)]">
            <span>
              {t('membersDrawer.summary.directMembers', { count: selectedMemberIds.size })}
            </span>
            <span aria-hidden className="h-3 w-px bg-[var(--color-divider)]" />
            <span>{t('membersDrawer.summary.departments', { count: selectedOrgIds.size })}</span>
            <span aria-hidden className="h-3 w-px bg-[var(--color-divider)]" />
            <span>
              {t('membersDrawer.summary.effectiveMembers', { count: effectiveMemberCount })}
            </span>
          </div>
        </div>
        {companyOptions.length > 1 ? (
          <div className="w-full max-w-[220px]">
            <Select
              aria-label={t('membersDrawer.companyLabel')}
              disabled={companiesQuery.isLoading || saveMutation.isPending}
              onValueChange={(value) => setSelectedCompanyId(value)}
              options={companyOptions}
              value={selectedCompanyId}
            />
          </div>
        ) : (
          <span className="shrink-0 text-xs font-medium text-[var(--color-text-muted)]">
            {companyOptions[0]?.label ?? t('membersDrawer.companyLoading')}
          </span>
        )}
      </div>

      {submitError ? (
        <div className="shrink-0 px-6 pt-4">
          <div className="rounded-md border border-[rgba(217,45,32,0.22)] bg-[rgba(217,45,32,0.06)] px-3 py-2 text-sm text-[var(--color-danger)]">
            {submitError}
          </div>
        </div>
      ) : null}

      {showLoadError ? (
        <div className="p-6">
          <ErrorState
            action={
              <Button
                className="mt-3"
                onClick={() => {
                  void companiesQuery.refetch()
                  void treeQuery.refetch()
                }}
                size="sm"
                variant="secondary"
              >
                {t('retry')}
              </Button>
            }
            description={formatApiError(loadError, t('membersDrawer.loadFailedDescription'))}
            title={t('membersDrawer.loadFailedTitle')}
          />
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-auto lg:grid-cols-[280px_minmax(0,1fr)_300px] lg:overflow-hidden">
          <section className="flex min-h-[320px] min-w-0 flex-col border-b border-[var(--color-divider)] bg-[#f8f9fb] lg:min-h-0 lg:border-r lg:border-b-0">
            <div className="shrink-0 border-b border-[var(--color-divider)] bg-[#fbfbfb] px-3 py-2">
              <div className="mb-1.5 flex h-8 items-center justify-between gap-2">
                <h3 className="text-sm font-bold text-[#374151]">
                  {t('membersDrawer.tree.title')}
                </h3>
                <button
                  className="flex h-8 cursor-pointer items-center gap-1 rounded-md px-2 text-xs font-medium text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-hover-muted)] hover:text-[var(--color-primary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-focus)]"
                  onClick={() =>
                    setOrgSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
                  }
                  type="button"
                >
                  {orgSortDirection === 'asc'
                    ? t('membersDrawer.tree.sortAsc')
                    : t('membersDrawer.tree.sortDesc')}
                  <ChevronIcon
                    className={cn(orgSortDirection === 'asc' ? 'rotate-90' : '-rotate-90')}
                  />
                </button>
              </div>
              <SearchInput
                aria-label={t('membersDrawer.tree.searchLabel')}
                className="!h-8 rounded-md"
                onChange={(event) => setOrgSearch(event.target.value)}
                placeholder={t('membersDrawer.tree.searchPlaceholder')}
                value={orgSearch}
              />
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {isInitialLoading ? (
                <div className="space-y-2 p-2">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <div
                      className="h-9 animate-pulse rounded-md bg-[var(--color-skeleton)]"
                      key={index}
                    />
                  ))}
                </div>
              ) : null}
              {!isInitialLoading && orgSearch.trim() ? (
                <div className="space-y-1">
                  {orgSearchResults.length > 0 ? (
                    orgSearchResults.map((result) => (
                      <button
                        className="w-full cursor-pointer rounded-md px-3 py-2 text-left transition-colors hover:bg-[rgba(25,91,255,0.08)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-focus)]"
                        key={result.id}
                        onClick={() => {
                          setSelectedDeptId(result.id)
                          setMemberPage(1)
                          expandOrgPath(result.id)
                          setHighlightedOrgId(result.id)
                          setOrgSearch('')
                        }}
                        type="button"
                      >
                        <span className="block truncate text-sm font-medium text-[var(--color-text)]">
                          {result.name}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-[var(--color-text-subtle)]">
                          {result.path}
                        </span>
                      </button>
                    ))
                  ) : (
                    <PaneEmpty>{t('membersDrawer.tree.noResults')}</PaneEmpty>
                  )}
                </div>
              ) : null}
              {!isInitialLoading && !orgSearch.trim() && root ? (
                <OrganizationTreeNode
                  compareOrgNames={compareOrgNames}
                  depth={0}
                  expandedOrgIds={expandedOrgIds}
                  highlightedOrgId={highlightedOrgId}
                  memberSelectionStateMap={memberSelectionStateMap}
                  node={root}
                  onSelect={(node) => {
                    setSelectedDeptId(node.id)
                    setMemberPage(1)
                  }}
                  onToggleExpand={handleToggleExpand}
                  onToggleOrg={handleToggleOrg}
                  orgSelectionStateMap={orgSelectionStateMap}
                  selectedDeptId={selectedDeptId}
                  t={t}
                />
              ) : null}
            </div>
          </section>

          <section className="flex min-h-[380px] min-w-0 flex-col border-b border-[var(--color-divider)] bg-white lg:min-h-0 lg:border-r lg:border-b-0">
            <div className="shrink-0 border-b border-[var(--color-divider)] px-3 py-2">
              <div className="mb-1.5 flex h-8 min-w-0 items-center justify-between gap-3">
                <div className="flex min-w-0 items-baseline gap-2">
                  <h3 className="truncate text-sm font-bold text-[#374151]">
                    {selectedDept?.name ?? t('membersDrawer.members.selectDepartment')}
                  </h3>
                  <p className="shrink-0 text-xs text-[var(--color-text-subtle)]">
                    {t('membersDrawer.members.scopeCount', { count: memberTotal })}
                  </p>
                </div>
                {visibleMembers.length > 0 ? (
                  <Button
                    className="shrink-0"
                    onClick={
                      allMembersInViewSelected ? handleDeselectAllInView : handleSelectAllInView
                    }
                    size="sm"
                    variant="ghost"
                  >
                    {allMembersInViewSelected
                      ? t('membersDrawer.members.deselectPage')
                      : t('membersDrawer.members.selectPage')}
                  </Button>
                ) : null}
              </div>
              <SearchInput
                aria-label={t('membersDrawer.members.searchLabel')}
                className="!h-8 rounded-md"
                onChange={(event) => {
                  setMemberSearch(event.target.value)
                  setMemberPage(1)
                }}
                placeholder={t('membersDrawer.members.searchPlaceholder')}
                value={memberSearch}
              />
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto bg-white px-3 py-2">
              {membersQuery.isLoading ? (
                <div className="space-y-1">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <div
                      className="h-12 animate-pulse rounded-md bg-[var(--color-skeleton)]"
                      key={index}
                    />
                  ))}
                </div>
              ) : membersQuery.isError ? (
                <ErrorState
                  action={
                    <Button
                      onClick={() => void membersQuery.refetch()}
                      size="sm"
                      variant="secondary"
                    >
                      {t('retry')}
                    </Button>
                  }
                  description={t('membersDrawer.members.loadFailedDescription')}
                  title={t('membersDrawer.members.loadFailedTitle')}
                />
              ) : visibleMembers.length > 0 ? (
                <div className="space-y-0.5">
                  {visibleMembers.map((member) => {
                    const isSelected = effectiveSelectedMemberIds.has(member.id)

                    return (
                      <button
                        aria-label={
                          isSelected
                            ? t('membersDrawer.members.unselectMember', { name: member.name })
                            : t('membersDrawer.members.selectMember', { name: member.name })
                        }
                        aria-pressed={isSelected}
                        className={cn(
                          'group flex min-h-[48px] w-full cursor-pointer items-center gap-2.5 rounded-md border bg-white px-2.5 py-1.5 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-focus)]',
                          isSelected
                            ? 'border-[rgba(25,91,255,0.42)] bg-[rgba(25,91,255,0.06)]'
                            : 'border-transparent hover:border-[rgba(25,91,255,0.26)] hover:bg-[rgba(25,91,255,0.04)]',
                        )}
                        key={member.id}
                        onClick={() => handleToggleMember(member.id)}
                        type="button"
                      >
                        <Avatar
                          className={cn(
                            'size-7 rounded-full text-xs',
                            isSelected
                              ? 'bg-[var(--color-primary)]'
                              : 'bg-[#e8ebf0] text-[#667085]',
                          )}
                          name={member.name}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold leading-5 text-[var(--color-text)]">
                            {member.name}
                          </span>
                          <span className="block truncate text-xs leading-4 text-[var(--color-text-muted)]">
                            {member.email || t('membersDrawer.members.noEmail')}
                          </span>
                        </span>
                        <MemberSelectionDot active={isSelected} />
                      </button>
                    )
                  })}
                </div>
              ) : (
                <PaneEmpty icon="users">
                  {selectedDept
                    ? memberSearch.trim()
                      ? t('membersDrawer.members.noSearchResults')
                      : t('membersDrawer.members.emptyScope')
                    : t('membersDrawer.members.selectDepartment')}
                </PaneEmpty>
              )}
            </div>
            {memberTotal > memberPageSize ? (
              <div className="flex shrink-0 items-center justify-between gap-3 border-t border-[var(--color-divider)] px-4 py-2">
                <Button
                  disabled={memberPage <= 1 || membersQuery.isFetching}
                  onClick={() => setMemberPage((current) => Math.max(1, current - 1))}
                  size="sm"
                  variant="ghost"
                >
                  {t('pagination.previous')}
                </Button>
                <span className="text-xs text-[var(--color-text-subtle)]">
                  {t('membersDrawer.members.pageStatus', {
                    page: memberPage,
                    total: memberPageCount,
                  })}
                </span>
                <Button
                  disabled={memberPage >= memberPageCount || membersQuery.isFetching}
                  onClick={() => setMemberPage((current) => Math.min(memberPageCount, current + 1))}
                  size="sm"
                  variant="ghost"
                >
                  {t('pagination.next')}
                </Button>
              </div>
            ) : null}
          </section>

          <section className="flex min-h-[320px] min-w-0 flex-col bg-[#f8f9fb] lg:min-h-0">
            <div className="shrink-0 border-b border-[var(--color-divider)] bg-[#fbfbfb] px-3 py-2">
              <div className="flex h-8 items-center">
                <h3 className="text-sm font-bold text-[#374151]">
                  {t('membersDrawer.selected.title')}
                </h3>
              </div>
              <div
                aria-label={t('membersDrawer.selected.tabsLabel')}
                className="mt-1.5 grid grid-cols-2 gap-2"
                role="tablist"
              >
                <button
                  aria-selected={selectedPanel === 'members'}
                  className={cn(
                    'h-8 cursor-pointer rounded-md border px-2 text-xs font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-focus)]',
                    selectedPanel === 'members'
                      ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                      : 'border-[var(--color-control-border)] bg-white text-[var(--color-text-muted)] hover:text-[var(--color-primary)]',
                  )}
                  onClick={() => setSelectedPanel('members')}
                  role="tab"
                  type="button"
                >
                  {t('membersDrawer.selected.membersTab', { count: selectedMemberIds.size })}
                </button>
                <button
                  aria-selected={selectedPanel === 'departments'}
                  className={cn(
                    'h-8 cursor-pointer rounded-md border px-2 text-xs font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-focus)]',
                    selectedPanel === 'departments'
                      ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                      : 'border-[var(--color-control-border)] bg-white text-[var(--color-text-muted)] hover:text-[var(--color-primary)]',
                  )}
                  onClick={() => setSelectedPanel('departments')}
                  role="tab"
                  type="button"
                >
                  {t('membersDrawer.selected.departmentsTab', { count: selectedOrgIds.size })}
                </button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {selectedPanel === 'members' ? (
                selectedUsersForPanel.length > 0 ? (
                  <div className="space-y-2">
                    {selectedUsersForPanel.map((item) => (
                      <button
                        className="w-full cursor-pointer rounded-lg border border-[var(--color-panel-border)] bg-white p-3 text-left transition-colors hover:border-[rgba(25,91,255,0.42)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-focus)]"
                        key={item.id}
                        onClick={() => {
                          if (item.primaryDeptId) {
                            setSelectedDeptId(item.primaryDeptId)
                            setMemberSearch(item.member.email ?? item.member.name)
                            setMemberPage(1)
                            expandOrgPath(item.primaryDeptId)
                          }
                        }}
                        type="button"
                      >
                        <span className="flex items-center gap-2">
                          <Avatar className="size-8" name={item.member.name} />
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-semibold text-[var(--color-text)]">
                              {item.member.name}
                            </span>
                            <span className="mt-0.5 block truncate text-xs text-[var(--color-text-subtle)]">
                              {item.deptPath}
                            </span>
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <PaneEmpty icon="users">{t('membersDrawer.selected.noMembers')}</PaneEmpty>
                )
              ) : selectedOrgsForPanel.length > 0 ? (
                <div className="space-y-2">
                  {selectedOrgsForPanel.map((item) => (
                    <button
                      className="w-full cursor-pointer rounded-lg border border-[var(--color-panel-border)] bg-white p-3 text-left transition-colors hover:border-[rgba(25,91,255,0.42)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-focus)]"
                      key={item.id}
                      onClick={() => {
                        setSelectedDeptId(item.id)
                        expandOrgPath(item.id)
                        setHighlightedOrgId(item.id)
                      }}
                      type="button"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <FolderIcon className="shrink-0 text-[var(--color-primary)]" />
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-[var(--color-text)]">
                            {item.name}
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-[var(--color-text-subtle)]">
                            {item.path}
                          </span>
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <PaneEmpty>{t('membersDrawer.selected.noDepartments')}</PaneEmpty>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  )

  if (embedded) {
    return scopeContent
  }

  return (
    <>
      <Drawer
        bodyClassName="overflow-hidden"
        confirmBeforeClose={confirmBeforeClosingDrawer}
        footer={footer}
        onOpenChange={onOpenChange}
        open={open}
        size="xl"
        title={title}
      >
        {scopeContent}
      </Drawer>

      <Modal
        closeLabel={t('membersDrawer.discardDialog.close')}
        footer={
          <>
            <Button onClick={() => setDiscardDialogOpen(false)} size="sm" variant="secondary">
              {t('membersDrawer.discardDialog.cancel')}
            </Button>
            <Button onClick={confirmDiscardChanges} size="sm" variant="danger">
              {t('membersDrawer.discardDialog.confirm')}
            </Button>
          </>
        }
        onClose={() => setDiscardDialogOpen(false)}
        open={discardDialogOpen}
        subtitle={t('membersDrawer.discardDialog.subtitle')}
        title={t('membersDrawer.discardDialog.title')}
      >
        <div className="rounded-md border border-[#ffd7a8] bg-[#fff8ec] px-3 py-2 text-sm leading-5 text-[#8a4b00]">
          {t('membersDrawer.discardDialog.message')}
        </div>
      </Modal>
    </>
  )
}
